import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * BeCaim 2.0 primitives ("bz").
 *
 * Thin React wrappers over the classes in src/styles/bz.css. Each one exists
 * because it encodes a rule of the design language (hairline structure, mono
 * for data, one accent, information-only colour) rather than to save typing.
 * Keep them small; put behaviour in pages, not here.
 */

export function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

/* ---------------------------------------------------------------- layout */

export function Panel({ title, meta, actions, children, className = '', bodyClass = '', testid }) {
  return (
    <section className={cx('bz-panel', className)} data-testid={testid}>
      {(title || actions) && (
        <header className="bz-panel-h">
          <div className="min-w-0 flex items-baseline gap-2">
            {title && <h2 className="text-[13px] font-semibold text-bz-ink truncate">{title}</h2>}
            {meta && <span className="bz-mono text-[11px] text-bz-ink-3 whitespace-nowrap">{meta}</span>}
          </div>
          {actions && <div className="flex items-center gap-1.5 flex-shrink-0">{actions}</div>}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function PageHeader({ eyebrow, title, intro, actions, children }) {
  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          {eyebrow && <div className="bz-label mb-1.5">{eyebrow}</div>}
          <h1 className="bz-display text-[28px] md:text-[32px] text-bz-ink">{title}</h1>
          {intro && <p className="mt-1.5 text-[14px] text-bz-ink-3 max-w-2xl">{intro}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Label({ children, className = '' }) {
  return <div className={cx('bz-label', className)}>{children}</div>;
}

export function Rule({ strong = false, className = '' }) {
  return <div className={cx(strong ? 'bz-rule-strong' : 'bz-rule', className)} />;
}

/* -------------------------------------------------------------- controls */

export function Button({ variant = 'outline', size, icon: Icon, children, className = '', as, to, ...rest }) {
  const cls = cx('bz-btn', `bz-btn-${variant}`, size === 'sm' && 'bz-btn-sm', size === 'lg' && 'bz-btn-lg', !children && Icon && 'bz-btn-icon', className);
  const inner = (<>{Icon && <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />}{children}</>);
  if (to) return <Link to={to} className={cls} {...rest}>{inner}</Link>;
  const Tag = as || 'button';
  return <Tag type={Tag === 'button' ? 'button' : undefined} className={cls} {...rest}>{inner}</Tag>;
}

export function Field({ label, help, children, className = '', htmlFor }) {
  return (
    <div className={className}>
      {label && <label htmlFor={htmlFor} className="bz-field-label">{label}</label>}
      {children}
      {help && <div className="bz-help">{help}</div>}
    </div>
  );
}

export function Input(props) { return <input {...props} className={cx('bz-input', props.className)} />; }
export function Textarea(props) { return <textarea {...props} className={cx('bz-textarea', props.className)} />; }
export function Select({ options = [], children, ...props }) {
  return (
    <select {...props} className={cx('bz-select', props.className)}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      {children}
    </select>
  );
}

export function Segmented({ value, onChange, options, className = '', ariaLabel }) {
  return (
    <div className={cx('bz-seg', className)} role="tablist" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} data-active={o.value === value}
          onClick={() => onChange(o.value)}>
          {o.label}{typeof o.count === 'number' && <span className="bz-mono ml-1.5 text-[11px] opacity-70">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- status */

const TONE_CLASS = {
  neutral: '', pos: 'bz-tag-pos', warn: 'bz-tag-warn', neg: 'bz-tag-neg', info: 'bz-tag-info', accent: 'bz-tag-accent', ink: 'bz-tag-ink',
  // Vivid Field domains and accents (styles/vivid.css)
  sales: 'bz-tag-sales', procurement: 'bz-tag-procurement', pink: 'bz-tag-pink', lime: 'bz-tag-lime', field: 'bz-tag-field',
};

export function Tag({ tone = 'neutral', mono = false, dot = false, children, className = '', title, style }) {
  return (
    <span className={cx('bz-tag', TONE_CLASS[tone], mono && 'bz-tag-mono', className)} title={title} style={style}>
      {dot && <i className={cx('bz-dot', tone !== 'neutral' && `bz-dot-${tone}`)} />}
      {children}
    </span>
  );
}

export function Dot({ tone = 'neutral', live = false, className = '' }) {
  return <i className={cx('bz-dot', tone !== 'neutral' && `bz-dot-${tone}`, live && 'bz-dot-live', className)} style={live ? { color: `var(--bz-${tone})` } : undefined} />;
}

export function Bar({ pct = 0, tone, className = '' }) {
  const v = Math.max(0, Math.min(100, Number(pct) || 0));
  return <div className={cx('bz-bar', tone && `bz-bar-${tone}`, className)} role="progressbar" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${v}%` }} /></div>;
}

/** Forecast / health → tone. Shared so every screen colours risk the same way. */
export function toneForForecast(status) {
  switch (status) {
    case 'behind': return 'neg';
    case 'at_risk': return 'warn';
    case 'ahead': case 'completed': return 'pos';
    case 'on_track': return 'accent';
    default: return 'neutral';
  }
}

/* ------------------------------------------------------------- numbers */

export function Num({ value, unit, className = '', compact = true, dash = '–' }) {
  const { i18n } = useTranslation();
  if (value === null || value === undefined || Number.isNaN(Number(value))) return <span className={cx('bz-mono', className)}>{dash}</span>;
  const n = Number(value);
  const fmt = new Intl.NumberFormat(i18n.language || 'en', compact && Math.abs(n) >= 10000 ? { notation: 'compact', maximumFractionDigits: 1 } : { maximumFractionDigits: Number.isInteger(n) ? 0 : 1 });
  return <span className={cx('bz-mono', className)}>{unit && /^[^\w\s]$/.test(unit) ? unit : ''}{fmt.format(n)}{unit && !/^[^\w\s]$/.test(unit) ? <span className="text-bz-ink-3 ml-1 font-ui text-[0.85em]">{unit}</span> : ''}</span>;
}

export function Mono({ children, className = '' }) { return <span className={cx('bz-mono', className)}>{children}</span>; }

export function timeAgo(iso, t) {
  if (!iso) return '';
  const d = new Date(iso); const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return t('c.bz.time.just_now');
  if (s < 3600) return t('c.bz.time.min_ago', { n: Math.floor(s / 60) });
  if (s < 86400) return t('c.bz.time.hr_ago', { n: Math.floor(s / 3600) });
  if (s < 86400 * 7) return t('c.bz.time.day_ago', { n: Math.floor(s / 86400) });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function Time({ iso, className = '' }) {
  const { t } = useTranslation();
  return <time dateTime={iso} title={iso ? new Date(iso).toLocaleString() : ''} className={cx('bz-mono text-[11px] text-bz-ink-3', className)}>{timeAgo(iso, t)}</time>;
}

/* --------------------------------------------------------------- "says" */

/**
 * The AI marker. What happened / why it matters / what next — always in that
 * order, always labelled, never decorated. `confidence` renders as a dot.
 */
export function Says({ label, children, confidence, footer, className = '' }) {
  const { t } = useTranslation();
  const tone = confidence == null ? null : confidence >= 75 ? 'pos' : confidence >= 45 ? 'warn' : 'neg';
  return (
    <div className={cx('bz-say', className)}>
      <div className="flex items-center gap-2 mb-1">
        <span className="bz-say-label">{label || t('c.bz.says.label')}</span>
        {tone && <span className="inline-flex items-center gap-1 text-[11px] text-bz-ink-3"><i className={`bz-dot bz-dot-${tone}`} />{t('c.bz.says.confidence', { n: Math.round(confidence) })}</span>}
      </div>
      <div className="text-[13.5px] leading-[1.5] text-bz-ink-2">{children}</div>
      {footer && <div className="mt-2 flex items-center gap-2 flex-wrap">{footer}</div>}
    </div>
  );
}

/* -------------------------------------------------------------- states */

export function Skeleton({ h = 14, w = '100%', className = '' }) {
  return <div className={cx('bz-skel', className)} style={{ height: h, width: w }} aria-hidden />;
}

export function SkeletonRows({ n = 4 }) {
  return (
    <div className="p-4 space-y-3" aria-busy>
      {Array.from({ length: n }).map((_, i) => <Skeleton key={i} w={`${90 - (i % 3) * 18}%`} />)}
    </div>
  );
}

export function Empty({ title, body, action, className = '', testid }) {
  return (
    <div className={cx('px-5 py-8 text-center', className)} data-testid={testid}>
      <p className="text-[14px] font-medium text-bz-ink">{title}</p>
      {body && <p className="mt-1 text-[13px] text-bz-ink-3 max-w-md mx-auto">{body}</p>}
      {action && <div className="mt-4 flex justify-center gap-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ title, body, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="px-5 py-6 flex items-start gap-3">
      <i className="bz-dot bz-dot-neg mt-1.5" />
      <div className="min-w-0">
        <p className="text-[13.5px] font-medium text-bz-ink">{title || t('c.bz.error.title')}</p>
        {body && <p className="mt-0.5 text-[12.5px] text-bz-ink-3">{String(body)}</p>}
        {onRetry && <button type="button" className="bz-btn bz-btn-outline bz-btn-sm mt-3" onClick={onRetry}>{t('c.bz.error.retry')}</button>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- flow */

/**
 * Process pipeline: counts along a workflow, left to right. Each step gets a
 * proportional bar relative to the first step so drop-off is visible without a
 * trapezoid. `steps = [{key, label, value, sub, href}]`.
 */
export function Flow({ steps, className = '', dense = false }) {
  const base = Math.max(1, Number(steps?.[0]?.value) || 0);
  return (
    <div className={cx('bz-flow', className)} role="list">
      {steps.map((s, i) => {
        const v = Number(s.value) || 0;
        const pct = Math.min(100, Math.round((v / base) * 100));
        const inner = (
          <>
            <div className={cx('bz-flow-n', dense && '!text-[18px]')}><Num value={v} compact /></div>
            <div className="bz-flow-l" title={s.label}>{s.label}</div>
            {s.sub && <div className="bz-flow-sub">{s.sub}</div>}
            <div className="bz-flow-bar"><i style={{ width: `${pct}%`, background: `var(--bz-seq-${Math.min(6, i + 1)})` }} /></div>
          </>
        );
        return s.href
          ? <Link key={s.key || i} to={s.href} role="listitem" className="bz-flow-step block hover:bg-bz-surface-2">{inner}</Link>
          : <div key={s.key || i} role="listitem" className="bz-flow-step">{inner}</div>;
      })}
    </div>
  );
}

/* -------------------------------------------------------------- spine */

export function Spine({ children, className = '' }) {
  return <div className={cx('bz-spine', className)}>{children}</div>;
}
export function SpineNode({ state = 'todo', title, meta, children, className = '' }) {
  return (
    <div className={cx('bz-spine-node pb-6 last:pb-0', className)} data-state={state}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[13.5px] font-semibold text-bz-ink">{title}</h3>
        {meta && <span className="bz-mono text-[11px] text-bz-ink-3 whitespace-nowrap">{meta}</span>}
      </div>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

/* -------------------------------------------------------------- tabs */

export function Tabs({ items, className = '' }) {
  // items: [{to, label, count, end}] — NavLink-like routing handled by caller via `active`.
  return (
    <nav className={cx('bz-tabs', className)} aria-label="Section">
      {items.map((it) => (
        <Link key={it.to} to={it.to} className="bz-tab" aria-current={it.active ? 'page' : undefined} data-testid={it.testid}>
          {it.icon && <it.icon className="w-3.5 h-3.5" />}{it.label}
          {typeof it.count === 'number' && it.count > 0 && <span className="bz-tab-count">{it.count}</span>}
        </Link>
      ))}
    </nav>
  );
}

export function Kbd({ children }) { return <kbd className="bz-kbd">{children}</kbd>; }

export default {
  Panel, PageHeader, Label, Rule, Button, Field, Input, Textarea, Select, Segmented, Tag, Dot, Bar, Num, Mono, Time,
  Says, Skeleton, SkeletonRows, Empty, ErrorState, Flow, Spine, SpineNode, Tabs, Kbd, cx, toneForForecast, timeAgo,
};
